import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import Button from '../UIKit/Button';
import { useTranslation } from 'react-i18next';

const UserBadgeSeriesProgressDialog = ({
  open,
  onClose,
  selectedUserSeries,
  loadingUserSeries,
  handleAwardSeriesReward,
  badges,
  getRarityColor,
  refreshUserSeriesProgress,
}) => {
  const [awardLoading, setAwardLoading] = useState(null);
  const { t } = useTranslation(['badges', 'common']);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <PeopleIcon sx={{ mr: 1 }} />
          {t('badges:badgeManagement.progress.userSeriesTitle')}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loadingUserSeries ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        ) : !selectedUserSeries ||
          selectedUserSeries.seriesProgress.length === 0 ? (
          <Typography>
            {t('badges:badgeManagement.progress.noUserSeriesData')}
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {selectedUserSeries?.user?.username ||
                  t('badges:badgeManagement.progress.user')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('badges:badgeManagement.progress.userSeriesProgress')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {(selectedUserSeries?.seriesProgress || []).map((progress) => (
                <Grid item xs={12} sm={6} key={progress._id}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={progress.image}
                        alt={progress.name}
                        sx={{ mr: 2, width: 40, height: 40 }}
                      />
                      <Box>
                        <Typography variant="subtitle1">
                          {progress.name}
                          {progress.isCompleted && (
                            <Chip
                              label={
                                progress.rewardClaimed
                                  ? t('badges:badgeManagement.progress.rewardClaimed')
                                  : t('badges:badgeManagement.progress.completed')
                              }
                              size="small"
                              color={
                                progress.rewardClaimed ? 'success' : 'primary'
                              }
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mt: 0.5,
                          }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={progress.progress}
                            sx={{
                              width: 100,
                              mr: 1,
                              height: 6,
                              borderRadius: 3,
                            }}
                            color={progress.isCompleted ? 'success' : 'primary'}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {Math.round(progress.progress)}% (
                            {progress.collectedBadges}/{progress.totalBadges})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Отображение значков серии */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {progress.badges && progress.badges.length > 0 ? (
                        progress.badges.map((badge) => {
                          // badge уже объект
                          const badgeObj = badge;
                          return (
                            <Tooltip
                              key={badgeObj._id}
                              title={`${badgeObj.name} ${
                                badgeObj.collected
                                  ? `(${t('badges:badgeManagement.progress.badgeEarned')})`
                                  : `(${t('badges:badgeManagement.progress.badgeNotEarned')})`
                              }`}
                              arrow
                            >
                              <Avatar
                                src={badgeObj.image}
                                alt={badgeObj.name}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  border: `2px solid ${getRarityColor(
                                    badgeObj.rarity
                                  )}`,
                                  opacity:
                                    badgeObj.isSecret && !badgeObj.collected
                                      ? 0.5
                                      : 1,
                                  filter: !badgeObj.collected
                                    ? 'grayscale(100%)'
                                    : 'none',
                                }}
                              />
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('badges:badgeManagement.progress.noBadgesInSeries')}
                        </Typography>
                      )}
                    </Box>

                    {progress.isCompleted && !progress.rewardClaimed && (
                      <Box mt={1}>
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          startIcon={<EmojiEventsIcon />}
                          disabled={awardLoading === progress._id}
                          onClick={async () => {
                            setAwardLoading(progress._id);
                            try {
                              await handleAwardSeriesReward(
                                selectedUserSeries.user._id,
                                progress._id
                              );
                              if (
                                typeof refreshUserSeriesProgress === 'function'
                              ) {
                                await refreshUserSeriesProgress();
                              }
                            } finally {
                              setAwardLoading(null);
                            }
                          }}
                        >
                          {awardLoading === progress._id
                            ? t('badges:badgeManagement.progress.awarding')
                            : t('badges:badgeManagement.progress.award')}
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
              {(selectedUserSeries?.seriesProgress || []).length === 0 && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{ p: 3, textAlign: 'center', borderRadius: '12px' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t('badges:badgeManagement.progress.noUserProgress')}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserBadgeSeriesProgressDialog;
